import React, { useContext, useState, useRef, useEffect } from "react";
import { Bell, Search, Settings, ChevronDown, User, Menu, LogOut, UserCircle2, ShieldCheck } from "lucide-react";
import { clsx } from "clsx";
import { useNavigate } from "react-router-dom"; // 추가
import { AuthContext } from "@context"; // 🌟 AuthContext 가져오기

export function Header({ title, onToggleSidebar }) {
  // 🌟 AuthContext에서 인증 정보(auth)와 로그아웃(logout) 함수 꺼내기
  const { auth, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // 🌟 실제 권한('ROLE_USER', 'ROLE_ADMIN')을 기준으로 분기 처리
  const isUser = auth?.role === 'ROLE_USER';

  // 이름은 auth.name(DB에 저장된 실제 회원명)을 우선 사용하고 없으면 기본값
  const displayName = auth?.name || (isUser ? '사용자' : '관리자');
  const displayRole = isUser ? '일반 사용자' : '시스템 관리자';

  const handleLogout = () => {
    setIsProfileOpen(false); // 드롭다운 닫기
    logout(); // 🌟 AuthContext의 실제 로그아웃 호출 (쿠키 삭제 및 화면 이동)
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="sticky top-0 z-20 flex h-16 w-full items-center justify-between border-b border-slate-200 bg-white px-6 shadow-sm">
      {/* Left: Hamburger & Title */}
      <div className="flex items-center gap-4">
        <button
          onClick={onToggleSidebar}
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          aria-label="Toggle sidebar">
          <Menu className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
      </div>

      {/* Right Side Actions */}
      <div className="flex items-center gap-6">
        {/* Search Bar */}
        <div className="relative hidden w-96 md:block">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="회사, 보고서 검색..."
            className="h-10 w-full rounded-full border border-slate-300 bg-slate-50 pl-10 pr-4 text-sm text-slate-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
        </div>

        {/* Notifications */}
        <button className="relative flex h-10 w-10 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 transition-colors">
          <Bell className="h-5 w-5" />
          {/* <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"></span> */}
        </button>

        {/* Divider */}
        <div className="h-8 w-px bg-slate-200"></div>

        {/* User Profile */}
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-colors focus:outline-none">
            
            <div className="h-10 w-10 overflow-hidden rounded-full border border-slate-200 bg-slate-100">
              {/* 역할에 따라 프로필 이미지 분기 (그대로 유지) */}
              {isUser ? (
                <UserCircle2 className="h-10 w-10" /> // 일반 사용자 아이콘
              ) : (
                <ShieldCheck className="h-10 w-10" /> // 관리자 아이콘
              )}
            </div>
            
            <div className="hidden text-left text-sm md:block">
              {/* 🌟 DB에서 가져온 실제 이름(auth.name) 출력 */}
              <p className="font-semibold text-slate-900 leading-tight">{displayName}</p>
              <p className="text-xs text-slate-500">{displayRole}</p>
            </div>
            <ChevronDown className={clsx("h-4 w-4 text-slate-400 transition-transform", isProfileOpen && "rotate-180")} />
          </button>

          {/* Dropdown Menu */}
          {isProfileOpen &&
          <div className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl border border-slate-100 bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none animate-in fade-in zoom-in duration-200 z-50">
              <div className="p-2">
                <div className="px-3 py-2 md:hidden border-b border-slate-100 mb-2">
                  <p className="font-semibold text-slate-900">{displayName}</p>
                  <p className="text-xs text-slate-500">{displayRole}</p>
                </div>
                <button onClick={() => { setIsProfileOpen(false); navigate('/profile'); }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                  <User className="h-4 w-4 text-slate-400" />
                  프로필 설정
                </button>
                <button onClick={() => { setIsProfileOpen(false); navigate('/account'); }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                  <Settings className="h-4 w-4 text-slate-400" />
                  계정 관리
                </button>
                <div className="my-1 h-px bg-slate-100" />
                <button
                onClick={handleLogout}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                  <LogOut className="h-4 w-4 text-red-500" />
                  로그아웃
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    </header>
  );
}